export default {
  namespaced: true,

  state: {
    driverTickets: [],
    currentDriverTicket: null,
    loading: false,
    statistics: {
      total: 0,
      new: 0,
      in_progress: 0,
      resolved: 0,
      closed: 0,
      escalated: 0,
    },
  },

  getters: {
    driverTickets: state => state.driverTickets,
    currentDriverTicket: state => state.currentDriverTicket,
    loading: state => state.loading,
    statistics: state => state.statistics,
  },

  mutations: {
    SET_DRIVER_TICKETS(state, tickets) {
      state.driverTickets = tickets
    },

    SET_CURRENT_DRIVER_TICKET(state, ticket) {
      state.currentDriverTicket = ticket
    },

    SET_LOADING(state, loading) {
      state.loading = loading
    },

    SET_STATISTICS(state, statistics) {
      state.statistics = statistics
    },

    UPDATE_DRIVER_TICKET(state, updatedTicket) {
      const index = state.driverTickets.findIndex(ticket => ticket.id === updatedTicket.id)
      if (index !== -1) {
        state.driverTickets.splice(index, 1, updatedTicket)
      }

      if (state.currentDriverTicket && state.currentDriverTicket.id === updatedTicket.id) {
        state.currentDriverTicket = updatedTicket
      }
    },

    ADD_DRIVER_TICKET(state, ticket) {
      state.driverTickets.unshift(ticket)
    },

    DELETE_DRIVER_TICKET(state, ticketId) {
      const index = state.driverTickets.findIndex(ticket => ticket.id === ticketId)
      if (index !== -1) {
        state.driverTickets.splice(index, 1)
      }

      if (state.currentDriverTicket && state.currentDriverTicket.id === ticketId) {
        state.currentDriverTicket = null
      }
    },
  },

  actions: {
    async fetchDriverTickets({ commit }, { queryParams = {} } = {}) {
      commit('SET_LOADING', true)

      try {
        const response = await this.$http.get('/api/support/driver-tickets/', {
          params: queryParams,
        })

        commit('SET_DRIVER_TICKETS', response.data.results || response.data)
        return response
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching driver tickets:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchDriverTicket({ commit }, ticketId) {
      commit('SET_LOADING', true)

      try {
        const response = await this.$http.get(`/api/support/driver-tickets/${ticketId}/`)
        commit('SET_CURRENT_DRIVER_TICKET', response.data)
        return response
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching driver ticket:', error)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async createDriverTicket({ commit, dispatch }, ticketData) {
      try {
        const response = await this.$http.post('/api/support/driver-tickets/', ticketData)
        commit('ADD_DRIVER_TICKET', response.data)

        // Update statistics after creating
        dispatch('fetchDriverTicketStatistics')

        return response
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating driver ticket:', error)
        throw error
      }
    },

    async updateDriverTicket({ commit, dispatch }, { id, data }) {
      try {
        const response = await this.$http.patch(`/api/support/driver-tickets/${id}/`, data)
        commit('UPDATE_DRIVER_TICKET', response.data)

        // Update statistics after updating
        dispatch('fetchDriverTicketStatistics')

        return response
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating driver ticket:', error)
        throw error
      }
    },

    async deleteDriverTicket({ commit, dispatch }, ticketId) {
      try {
        await this.$http.delete(`/api/support/driver-tickets/${ticketId}/`)
        commit('DELETE_DRIVER_TICKET', ticketId)

        // Update statistics after deleting
        dispatch('fetchDriverTicketStatistics')
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error deleting driver ticket:', error)
        throw error
      }
    },

    async takeDriverTicketInCharge({ commit, dispatch }, ticketId) {
      try {
        const response = await this.$http.post(`/api/support/driver-tickets/${ticketId}/take_in_charge/`)
        commit('UPDATE_DRIVER_TICKET', response.data)

        // Update statistics
        dispatch('fetchDriverTicketStatistics')

        return response
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error taking driver ticket in charge:', error)
        throw error
      }
    },

    async resolveDriverTicket({ commit, dispatch }, { id, response: supportResponse }) {
      try {
        const response = await this.$http.post(`/api/support/driver-tickets/${id}/resolve/`, {
          support_response: supportResponse,
        })
        commit('UPDATE_DRIVER_TICKET', response.data)

        // Update statistics
        dispatch('fetchDriverTicketStatistics')

        return response
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error resolving driver ticket:', error)
        throw error
      }
    },

    async escalateDriverTicket({ commit, dispatch }, { id, note }) {
      try {
        const response = await this.$http.post(`/api/support/driver-tickets/${id}/escalate/`, {
          note,
        })
        commit('UPDATE_DRIVER_TICKET', response.data)

        // Update statistics
        dispatch('fetchDriverTicketStatistics')

        return response
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error escalating driver ticket:', error)
        throw error
      }
    },

    async closeDriverTicket({ commit, dispatch }, ticketId) {
      try {
        const response = await this.$http.post(`/api/support/driver-tickets/${ticketId}/close/`)
        commit('UPDATE_DRIVER_TICKET', response.data)

        // Update statistics
        dispatch('fetchDriverTicketStatistics')

        return response
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error closing driver ticket:', error)
        throw error
      }
    },

    async addDriverTicketContactAttempt({ commit, dispatch }, { id }) {
      try {
        const response = await this.$http.post(`/api/support/driver-tickets/${id}/add_contact_attempt/`)
        commit('UPDATE_DRIVER_TICKET', response.data)

        // Update statistics
        dispatch('fetchDriverTicketStatistics')

        return response
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error adding contact attempt:', error)
        throw error
      }
    },

    async fetchDriverTicketStatistics({ commit }) {
      try {
        const response = await this.$http.get('/api/support/driver-tickets/statistics/')
        commit('SET_STATISTICS', response.data)
        return response
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching driver ticket statistics:', error)
        // Set default statistics on error
        commit('SET_STATISTICS', {
          total: 0,
          new: 0,
          in_progress: 0,
          resolved: 0,
          closed: 0,
          escalated: 0,
        })
        throw error
      }
    },

    // Bulk operations
    async bulkUpdateDriverTickets({ dispatch }, { ids, data }) {
      try {
        const response = await this.$http.patch('/api/support/driver-tickets/bulk_update/', {
          ids,
          data,
        })

        // Refresh the list after bulk update
        dispatch('fetchDriverTickets')
        dispatch('fetchDriverTicketStatistics')

        return response
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error bulk updating driver tickets:', error)
        throw error
      }
    },

    async exportDriverTickets(context, { format = 'excel', filters = {} } = {}) {
      try {
        const response = await this.$http.get('/api/support/driver-tickets/export/', {
          params: {
            format,
            ...filters,
          },
          responseType: 'blob',
        })

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `driver-tickets-${new Date().toISOString().split('T')[0]}.${format}`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        return response
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error exporting driver tickets:', error)
        throw error
      }
    },

    // Clear data
    clearDriverTickets({ commit }) {
      commit('SET_DRIVER_TICKETS', [])
      commit('SET_CURRENT_DRIVER_TICKET', null)
    },

    clearCurrentDriverTicket({ commit }) {
      commit('SET_CURRENT_DRIVER_TICKET', null)
    },
  },
}
